import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import {
  batchesApi,
  competitionsApi,
  sportsApi,
  attributionsApi,
  studentsByGrApi,
  certBranchesApi,
  CERT_TYPES,
} from "../../../service/certificate"
import { getAllSessions } from "../../../service/sessions"

// Shared RMS-parity batch builder (our theme). One component drives every
// certificate type via a per-type field config: pick Session + Campus → type a
// GR per row → lookup auto-fills name/class/section/father/gender → fill the
// type-specific fields (text / number / select / master-data select / structured
// events) → save as a batch (ref number). Template/PDF happen later from the
// saved batch. Mirrors RMS's per-type forms without duplicating the shell.

export type FieldConfig =
  | { kind: "text"; key: string; label: string; placeholder?: string }
  | { kind: "number"; key: string; label: string }
  | { kind: "select"; key: string; label: string; options: { value: string; label: string }[] }
  | { kind: "masterSelect"; key: string; label: string; source: "attributions" | "competitions" | "sports" }
  | { kind: "events"; key: string; label: string }
  | { kind: "singleEvent"; key: string; label: string }

type EventRow = { key: string; event_type: "competition" | "sport"; event_ref_id: string; event_name: string }
type Row = {
  key: string
  gr_number: string
  loading?: boolean
  resolved?: boolean
  error?: string | null
  student_id?: string | null
  full_name?: string
  class_name?: string
  section_name?: string
  fathers_name?: string
  gender?: string
  extras: Record<string, string>
  events: EventRow[]
}

const uid = () =>
  typeof crypto !== "undefined" && (crypto as any).randomUUID ? (crypto as any).randomUUID() : String(Math.random())
const emptyEvent = (): EventRow => ({ key: uid(), event_type: "competition", event_ref_id: "", event_name: "" })
const emptyRow = (): Row => ({ key: uid(), gr_number: "", resolved: false, extras: {}, events: [emptyEvent()] })
const asArray = (x: any): any[] =>
  Array.isArray(x) ? x : x?.data?.items || x?.items || x?.data || x?.sessions || []
const slug = (s: string) => String(s).trim().replace(/[^A-Za-z0-9._-]+/g, "_").replace(/^_+|_+$/g, "")
const typeLabelOf = (v: string) => CERT_TYPES.find(t => t.value === v)?.label || v

const BatchRosterForm = ({
  type,
  fields,
  recipientEditable = false,
  onSaved,
}: {
  type: string
  fields: FieldConfig[]
  recipientEditable?: boolean
  onSaved?: () => void
}) => {
  const [sessions, setSessions] = useState<any[]>([])
  const [campuses, setCampuses] = useState<any[]>([])
  const [master, setMaster] = useState<Record<string, any[]>>({})

  const [sessionId, setSessionId] = useState("")
  const [campusId, setCampusId] = useState("")
  const [fileName, setFileName] = useState("")
  const [fileNameTouched, setFileNameTouched] = useState(false)
  const [rows, setRows] = useState<Row[]>([emptyRow()])
  const [saving, setSaving] = useState(false)

  const needsEvents = useMemo(() => fields.some(f => f.kind === "events"), [fields])
  const eventsKey = useMemo(() => fields.find(f => f.kind === "events")?.key, [fields])

  useEffect(() => {
    getAllSessions({ limit: 1000, page: 1 }).then(r => setSessions(asArray(r))).catch(() => {})
    certBranchesApi().then(r => setCampuses(r.data || [])).catch(() => {})
    const wants = new Set<string>()
    fields.forEach(f => {
      if (f.kind === "masterSelect") wants.add(f.source)
      if (f.kind === "events" || f.kind === "singleEvent") { wants.add("competitions"); wants.add("sports") }
    })
    if (wants.has("attributions")) attributionsApi.list().then(r => setMaster(m => ({ ...m, attributions: asArray(r) }))).catch(() => {})
    if (wants.has("competitions")) competitionsApi.list().then(r => setMaster(m => ({ ...m, competitions: asArray(r) }))).catch(() => {})
    if (wants.has("sports")) sportsApi.list().then(r => setMaster(m => ({ ...m, sports: asArray(r) }))).catch(() => {})
  }, [fields])

  // Auto-build file name: Session_Campus_<Type>_date.
  useEffect(() => {
    if (fileNameTouched) return
    const sName = sessions.find(s => s._id === sessionId)?.title || sessions.find(s => s._id === sessionId)?.name || "Session"
    const cName = campusId ? campuses.find(c => c._id === campusId)?.name || "" : "All_Campuses"
    const d = new Date().toISOString().slice(0, 10)
    setFileName([slug(sName), slug(cName), slug(typeLabelOf(type)), d].filter(Boolean).join("_"))
  }, [sessionId, campusId, sessions, campuses, fileNameTouched, type])

  const setRow = (i: number, patch: Partial<Row>) => setRows(a => a.map((r, j) => (j === i ? { ...r, ...patch } : r)))
  const setExtra = (i: number, key: string, val: string) =>
    setRows(a => a.map((r, j) => (j === i ? { ...r, extras: { ...r.extras, [key]: val } } : r)))
  const addRow = () => setRows(a => [...a, emptyRow()])
  const removeRow = (i: number) => setRows(a => (a.length === 1 ? [emptyRow()] : a.filter((_, j) => j !== i)))

  const setEvent = (ri: number, ei: number, patch: Partial<EventRow>) =>
    setRows(a => a.map((r, j) => (j === ri ? { ...r, events: r.events.map((e, k) => (k === ei ? { ...e, ...patch } : e)) } : r)))
  const addEvent = (ri: number) => setRows(a => a.map((r, j) => (j === ri ? { ...r, events: [...r.events, emptyEvent()] } : r)))
  const removeEvent = (ri: number, ei: number) =>
    setRows(a => a.map((r, j) => {
      if (j !== ri) return r
      const next = r.events.filter((_, k) => k !== ei)
      return { ...r, events: next.length ? next : [emptyEvent()] }
    }))

  const lookupGr = async (i: number) => {
    const row = rows[i]
    if (!row?.gr_number?.trim()) return
    if (!sessionId) { setRow(i, { error: "Pick a session first", resolved: false }); return }
    setRow(i, { loading: true, error: null })
    try {
      const res = await studentsByGrApi({ session: sessionId, grNumbers: row.gr_number.trim() })
      const s = (res.data || [])[0]
      if (!s) { setRow(i, { loading: false, resolved: false, student_id: null, error: "GR not found in this session" }); return }
      setRows(a => {
        const next = a.map((r, j) => (j === i ? {
          ...r, loading: false, resolved: true, error: null,
          student_id: s.studentId, full_name: r.full_name && recipientEditable ? r.full_name : s.name,
          class_name: s.className || "", section_name: s.sectionName || "",
          fathers_name: s.fathersName || "", gender: s.gender || "",
        } : r))
        return i === next.length - 1 ? [...next, emptyRow()] : next
      })
    } catch (err: any) {
      setRow(i, { loading: false, resolved: false, error: err?.response?.data?.message || "Lookup failed" })
    }
  }

  // A row is ready when: (recipientEditable → has a name) else (GR resolved),
  // AND every configured field that's required has a value / an event.
  const rowReady = (r: Row): boolean => {
    if (recipientEditable ? !r.full_name?.trim() : !(r.resolved && r.student_id)) return false
    if (needsEvents) return r.events.some(e => e.event_ref_id && e.event_name)
    return true
  }
  const readyCount = useMemo(() => rows.filter(rowReady).length, [rows, needsEvents, recipientEditable])

  const save = async () => {
    if (!sessionId) return toast.error("Pick a session.")
    const chosen = rows.filter(rowReady)
    if (!chosen.length) return toast.error("Add at least one complete row.")
    setSaving(true)
    try {
      const studentsData = chosen.map(r => {
        const base: any = {
          student_id: r.student_id || null,
          full_name: r.full_name || "",
          gr_number: r.gr_number.trim(),
          class_name: r.class_name || "",
          section_name: r.section_name || "",
          fathers_name: r.fathers_name || "",
          gender: r.gender || "",
          ...r.extras,
        }
        // General's recipient is typed into the Name cell — buildContext reads
        // it as `recipient_name`, so mirror it there.
        if (recipientEditable) base.recipient_name = r.full_name || ""
        if (needsEvents && eventsKey) {
          base[eventsKey] = r.events
            .filter(e => e.event_ref_id && e.event_name)
            .map(e => ({ event_type: e.event_type, event_ref_id: e.event_ref_id, event_name: e.event_name }))
        }
        return base
      })
      const res = await batchesApi.create({ type, fileName: fileName.trim(), session: sessionId, branch: campusId || undefined, studentsData })
      toast.success(`Saved ${chosen.length} student(s)${res.data?.refNumber ? ` — ${res.data.refNumber}` : ""}`)
      setRows([emptyRow()])
      setFileNameTouched(false)
      onSaved?.()
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Save failed")
    } finally {
      setSaving(false)
    }
  }

  const renderField = (r: Row, i: number, f: FieldConfig) => {
    if (f.kind === "events") {
      if (!r.resolved) return <span className="text-muted small">Resolve the GR first to add events.</span>
      return (
        <div className="d-flex flex-column gap-1">
          {r.events.map((ev, ei) => {
            const opts = ev.event_type === "sport" ? master.sports || [] : master.competitions || []
            return (
              <div key={ev.key} className="d-flex align-items-center gap-1">
                <select className="form-select form-select-sm" style={{ width: 130 }} value={ev.event_type}
                  onChange={e => setEvent(i, ei, { event_type: e.target.value as any, event_ref_id: "", event_name: "" })}>
                  <option value="competition">Competition</option>
                  <option value="sport">Sport</option>
                </select>
                <select className="form-select form-select-sm" style={{ minWidth: 170 }} value={ev.event_ref_id}
                  onChange={e => setEvent(i, ei, { event_ref_id: e.target.value, event_name: opts.find((o: any) => o._id === e.target.value)?.name || "" })}>
                  <option value="">— Select event —</option>
                  {opts.map((o: any) => <option key={o._id} value={o._id}>{o.name}</option>)}
                </select>
                <button className="btn btn-sm btn-outline-danger" onClick={() => removeEvent(i, ei)}>✕</button>
              </div>
            )
          })}
          <button className="btn btn-sm btn-outline-secondary align-self-start" onClick={() => addEvent(i)}>+ Add event</button>
        </div>
      )
    }
    if (f.kind === "singleEvent") {
      const etype = r.extras.event_type || "competition"
      const opts = etype === "sport" ? master.sports || [] : master.competitions || []
      return (
        <div className="d-flex align-items-center gap-1">
          <select className="form-select form-select-sm" style={{ width: 120 }} value={etype}
            onChange={e => { setExtra(i, "event_type", e.target.value); setExtra(i, "event_name", "") }}>
            <option value="competition">Competition</option>
            <option value="sport">Sport</option>
          </select>
          <select className="form-select form-select-sm" style={{ minWidth: 160 }} value={r.extras.event_name || ""}
            onChange={e => setExtra(i, "event_name", e.target.value)}>
            <option value="">— Select —</option>
            {opts.map((o: any) => <option key={o._id} value={o.name}>{o.name}</option>)}
          </select>
        </div>
      )
    }
    const val = r.extras[f.key] || ""
    if (f.kind === "masterSelect") {
      const opts = master[f.source] || []
      return (
        <select className="form-select form-select-sm" value={val}
          onChange={e => { const name = opts.find((o: any) => o._id === e.target.value)?.name || ""; setExtra(i, f.key, name) }}>
          <option value="">— Select —</option>
          {opts.map((o: any) => <option key={o._id} value={o.name}>{o.name}</option>)}
        </select>
      )
    }
    if (f.kind === "select") {
      return (
        <select className="form-select form-select-sm" value={val} onChange={e => setExtra(i, f.key, e.target.value)}>
          <option value="">—</option>
          {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )
    }
    return (
      <input type={f.kind === "number" ? "number" : "text"} className="form-control form-control-sm"
        placeholder={(f as any).placeholder} value={val} onChange={e => setExtra(i, f.key, e.target.value)} />
    )
  }

  return (
    <>
      <div className="card">
        <div className="card-header"><h5 className="mb-0">1. Scope</h5></div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Session *</label>
              <select className="form-select" value={sessionId} onChange={e => setSessionId(e.target.value)}>
                <option value="">Select…</option>
                {sessions.map(s => <option key={s._id} value={s._id}>{s.title || s.name}</option>)}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label">Campus</label>
              <select className="form-select" value={campusId} onChange={e => setCampusId(e.target.value)}>
                <option value="">All Campuses</option>
                {campuses.map(c => <option key={c._id} value={c._id}>{c.campusNo ? `${c.campusNo} — ` : ""}{c.name}</option>)}
              </select>
              <small className="text-muted">Leave blank to look up GR numbers across every campus.</small>
            </div>
          </div>
        </div>
      </div>

      <div className="card mt-3">
        <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h5 className="mb-0">2. Students &amp; details ({readyCount} ready)</h5>
          <div className="d-flex align-items-center gap-2">
            <input className="form-control form-control-sm" style={{ width: 320 }} placeholder="Batch file name"
              value={fileName} onChange={e => { setFileName(e.target.value); setFileNameTouched(true) }} />
            <button className="btn btn-primary" onClick={save} disabled={saving}>
              {saving ? "Saving…" : `Save ${readyCount || ""} student${readyCount === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
        <div className="card-body table-responsive">
          <table className="table table-bordered align-middle mb-2">
            <thead>
              <tr>
                <th style={{ width: 36 }}>#</th>
                <th style={{ width: 150 }}>GR Number</th>
                <th>Name</th>
                <th style={{ width: 120 }}>Class</th>
                {fields.map(f => <th key={f.key}>{f.label}</th>)}
                <th style={{ width: 36 }} />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.key}>
                  <td className="text-muted">{i + 1}</td>
                  <td>
                    <input className={`form-control form-control-sm ${r.error ? "is-invalid" : r.resolved ? "is-valid" : ""}`}
                      placeholder="e.g. 639010" value={r.gr_number}
                      onChange={e => setRow(i, { gr_number: e.target.value, resolved: false })}
                      onBlur={() => lookupGr(i)} onKeyDown={e => e.key === "Enter" && lookupGr(i)} />
                    {r.loading && <small className="text-muted">Looking up…</small>}
                    {r.error && <small className="text-danger">{r.error}</small>}
                  </td>
                  <td>
                    {recipientEditable ? (
                      <input className="form-control form-control-sm" placeholder="Recipient name"
                        value={r.full_name || ""} onChange={e => setRow(i, { full_name: e.target.value })} />
                    ) : r.resolved ? (
                      <>
                        <div>{r.full_name}</div>
                        <small className="text-muted">{[r.fathers_name && `s/o ${r.fathers_name}`, r.gender].filter(Boolean).join(" · ")}</small>
                      </>
                    ) : <span className="text-muted">—</span>}
                  </td>
                  <td>{r.resolved ? `${r.class_name || ""}${r.section_name ? ` (${r.section_name})` : ""}` : <span className="text-muted">—</span>}</td>
                  {fields.map(f => <td key={f.key}>{renderField(r, i, f)}</td>)}
                  <td><button className="btn btn-sm btn-outline-danger" onClick={() => removeRow(i)}>✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <button className="btn btn-sm btn-outline-primary" onClick={addRow}>+ Add row</button>
        </div>
      </div>
    </>
  )
}

export default BatchRosterForm
